import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import RecordPage, { FormSection } from '../components/shell/RecordPage';
import PasswordField, { RequireChangeCheckbox } from '../components/shell/PasswordField';
import { paneForm } from '../components/shell/SidePane';
import { StatusBadge } from '../components/datagrid/DataGrid';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User,
  Envelope,
  Key,
  ShieldCheck,
  FloppyDisk,
  GraduationCap,
  IdentificationCard
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ProfileSettings() {
  const { api, user, setUser } = useAuth();
  const profileFormRef = useRef(null);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    guardian_name: '',
    student_level: '',
    school_class: '',
    school_name: '',
    school_board: '',
    college_year: '',
    college_name: '',
    professional_course: '',
    professional_level: '',
    competitive_exam: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [requireChange, setRequireChange] = useState(false);
  const [passwordPaneOpen, setPasswordPaneOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      }));
    }
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/auth/me');
      setProfileData({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        email: res.data.email || '',
        guardian_name: res.data.guardian_name || '',
        student_level: res.data.student_level || '',
        school_class: res.data.school_class || '',
        school_name: res.data.school_name || '',
        school_board: res.data.school_board || '',
        college_year: res.data.college_year || '',
        college_name: res.data.college_name || '',
        professional_course: res.data.professional_course || '',
        professional_level: res.data.professional_level || '',
        competitive_exam: res.data.competitive_exam || ''
      });
      setDirty(false);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const updateProfile = (patch) => {
    setProfileData(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await api.put('/auth/profile', profileData);
      toast.success('Profile updated successfully');
      setDirty(false);

      // Update local user state
      if (res.data.user) {
        setUser(res.data.user);
        localStorage.setItem('lms_user', JSON.stringify(res.data.user));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      // Signed-in users set a new password directly — no current password, no confirm field
      await api.put('/auth/change-password', { new_password: newPassword, require_change: requireChange });
      toast.success(requireChange ? 'Password changed — you will be asked to set a new one at your next sign-in' : 'Password changed successfully');
      setNewPassword('');
      setRequireChange(false);
      setPasswordPaneOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  // Toolbar actions submit the respective <form> so native validation (required / minLength) still runs
  const submitForm = (ref) => {
    const form = ref.current;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const roleLabel = user?.role?.replace('_', ' ') || '';
  const contact = user?.email || user?.phone || '';

  // Change password → resizable side pane (no popup, no section on the page)
  const closePasswordPane = () => { setPasswordPaneOpen(false); setNewPassword(''); setRequireChange(false); };
  let side = null;
  if (passwordPaneOpen) {
    const f = paneForm({
      id: 'change-password-form', onSubmit: handleChangePassword, onCancel: closePasswordPane,
      submitLabel: 'Change password', submitting: savingPassword, submitIcon: Key,
      children: (
        <>
          <PasswordField
            id="new_password"
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            generate
            required
            placeholder="At least 6 characters"
            hint="Use the dice to generate a strong password; the eye shows it and the copy icon copies it. Your current password is not needed."
            testid="profile-new-password"
          />
          <RequireChangeCheckbox
            checked={requireChange}
            onCheckedChange={setRequireChange}
            label="Ask me to set a new password at next sign-in"
            description="Useful when you set a temporary password now and want to choose the final one later."
            testid="profile-require-change"
          />
          <p className="field-hint">The new password takes effect immediately; other devices will need to sign in again with it.</p>
        </>
      ),
    });
    side = { open: true, onOpenChange: closePasswordPane, title: 'Change password', subtitle: contact, icon: Key, children: f.body, footer: f.footer, testid: 'change-password-pane', storageKey: 'pane-change-password' };
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="profile-settings-page"
        title="My profile"
        subtitle={[contact, roleLabel].filter(Boolean).join(' · ')}
        badge={user?.role && <StatusBadge value={user.role} />}
        onBack={null}
        onRefresh={fetchProfile}
        dirty={dirty}
        side={side}
        primary={[
          { key: 'save', label: savingProfile ? 'Saving…' : 'Save profile', icon: FloppyDisk, onClick: () => submitForm(profileFormRef), disabled: savingProfile, testid: 'save-profile-btn' },
        ]}
        secondary={[
          { key: 'password', label: 'Change password', icon: Key, onClick: () => setPasswordPaneOpen((o) => !o), testid: 'change-password-btn', title: 'Set a new password (opens a side pane)' },
        ]}
      >
        {/* Account Overview (identity + account facts in one section) */}
        <FormSection title="Account Overview" icon={ShieldCheck}>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 md:min-w-[320px]">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-tight truncate">{user?.first_name} {user?.last_name}</h3>
                <p className="text-[13px] text-muted-foreground truncate">{contact}</p>
              </div>
            </div>
            <dl className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-[13px] md:border-l md:border-border md:pl-5" data-testid="account-facts">
              <div><dt className="text-xs text-muted-foreground" data-find="Role">Role</dt><dd className="mt-0.5"><StatusBadge value={user?.role || ''} /></dd></div>
              <div><dt className="text-xs text-muted-foreground" data-find="Account Status">Account status</dt><dd className="mt-0.5"><StatusBadge value={user?.is_active === false ? 'inactive' : 'active'} /></dd></div>
              <div><dt className="text-xs text-muted-foreground" data-find="Account ID">Account ID</dt><dd className="mt-0.5 font-mono" title={user?.id}>{user?.id?.substring(0, 8)}…</dd></div>
              <div><dt className="text-xs text-muted-foreground" data-find="Member since">Member since</dt><dd className="mt-0.5">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</dd></div>
            </dl>
          </div>
        </FormSection>

        {/* Profile Information */}
        <FormSection title="Profile Information" icon={User} description="Update your personal details">
          <form ref={profileFormRef} onSubmit={handleUpdateProfile} className="space-y-3" data-testid="profile-form">
            <div className="form-grid">
              <div className="field">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={profileData.first_name}
                  onChange={(e) => updateProfile({ first_name: e.target.value })}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="field">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={profileData.last_name}
                  onChange={(e) => updateProfile({ last_name: e.target.value })}
                  placeholder="Enter last name"
                  required
                />
              </div>

            <div className="field">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Envelope className="w-4 h-4" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                onChange={(e) => updateProfile({ email: e.target.value })}
                placeholder="Enter email address"
                required
              />
              <p className="field-hint">
                Changing your email will require you to log in again with the new email
              </p>
            </div>
            </div>
            {/* Hidden submit so Enter in a field still submits; the toolbar "Save profile" calls requestSubmit() */}
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        </FormSection>

        {/* Student Details - only show for learners with student_level */}
        {user?.role === 'learner' && profileData.student_level && (
          <FormSection title="Student Details" icon={GraduationCap} description="Your academic information" bodyClassName="p-3 space-y-3">
            <div className="form-grid">
              {profileData.guardian_name && (
                <div>
                  <Label className="text-xs text-muted-foreground">Guardian Name</Label>
                  <p className="font-medium">{profileData.guardian_name}</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Student Level</Label>
                <p className="font-medium capitalize">{profileData.student_level}</p>
              </div>
            </div>

            {profileData.student_level === 'school' && (
              <div className="form-grid p-3 bg-muted/30 rounded-lg">
                {profileData.school_class && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Class</Label>
                    <p className="font-medium">Class {profileData.school_class}</p>
                  </div>
                )}
                {profileData.school_board && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Board</Label>
                    <p className="font-medium">{profileData.school_board}</p>
                  </div>
                )}
                {profileData.school_name && (
                  <div>
                    <Label className="text-xs text-muted-foreground">School Name</Label>
                    <p className="font-medium">{profileData.school_name}</p>
                  </div>
                )}
              </div>
            )}

            {profileData.student_level === 'college' && (
              <div className="form-grid p-3 bg-muted/30 rounded-lg">
                {profileData.college_year && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Year</Label>
                    <p className="font-medium">{profileData.college_year}</p>
                  </div>
                )}
                {profileData.college_name && (
                  <div>
                    <Label className="text-xs text-muted-foreground">College Name</Label>
                    <p className="font-medium">{profileData.college_name}</p>
                  </div>
                )}
              </div>
            )}

            {profileData.student_level === 'competitive' && profileData.competitive_exam && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <Label className="text-xs text-muted-foreground">Exam Type</Label>
                <p className="font-medium">{profileData.competitive_exam}</p>
              </div>
            )}

            {profileData.student_level === 'professional' && (
              <div className="form-grid p-3 bg-muted/30 rounded-lg">
                {profileData.professional_course && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Course</Label>
                    <p className="font-medium">{profileData.professional_course}</p>
                  </div>
                )}
                {profileData.professional_level && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Level</Label>
                    <p className="font-medium">{profileData.professional_level}</p>
                  </div>
                )}
              </div>
            )}
          </FormSection>
        )}

      </RecordPage>
    </DashboardLayout>
  );
}
